import { Node, useReactFlow } from '@xyflow/react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import { LoadingIcon } from 'src/components/icons/LoadingIcon';
import IndeterminateCheckbox from 'src/components/indeterminate-checkbox';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { exportConfiguration } from 'src/utils/export-configuration';
import { twMerge } from 'tailwind-merge';

import { NodeCategoryModel } from '../../models/node-data';
import { globalSettingsDefault } from '../global-settings';

interface StandaloneTabProps {
  allNodeSwitches: Node[];
}

export function StandaloneTab({ allNodeSwitches }: StandaloneTabProps) {
  const { vlans, globalSettings } = useGlobalContext();
  const { getNodes, getEdges } = useReactFlow();
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [isExportingIds, setIsExportingIds] = useState<string[]>([]);
  const nodes = getNodes();
  const edges = getEdges();

  const [configurationName, setConfigurationName] = useState<
    Record<string, string>
  >({});

  const exportNode = async (nodeId: string) => {
    setIsExportingIds((old) => [...old, nodeId]);
    try {
      await exportConfiguration({
        nodes,
        edges,
        vlans,
        targetId: nodeId,
        cfgName: configurationName[nodeId],
        globalSettings: globalSettings ?? globalSettingsDefault,
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsExportingIds((old) => old.filter((id) => id !== nodeId));
      toast.success('Configuration exported successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('exportConfiguration ~ error:', error);
      toast.error('Failed to export configuration');
    } finally {
      setIsExportingIds((old) => old.filter((id) => id !== nodeId));
    }
  };

  const exportSelected = async () => {
    selectedNodes.forEach(async (nodeId) => {
      setIsExportingIds((old) => [...old, nodeId]);
      try {
        await exportConfiguration({
          nodes,
          edges,
          vlans,
          targetId: nodeId,
          cfgName: configurationName[nodeId],
          globalSettings: globalSettings ?? globalSettingsDefault,
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsExportingIds((old) => old.filter((id) => id !== nodeId));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('exportConfiguration ~ error:', error);
        toast.error(
          `Failed to export configuration for ${configurationName[nodeId]}`,
        );
      } finally {
        setIsExportingIds((old) => old.filter((id) => id !== nodeId));
      }
    });
    toast.success('Configurations exported successfully');
  };

  useEffect(() => {
    setSelectedNodes([]);
    setConfigurationName(
      allNodeSwitches.reduce(
        (acc, node) => {
          acc[node.id as string] = (
            node.data?.category as NodeCategoryModel
          )?.name;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="relative max-h-96 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 overflow-y-auto">
          <tbody className="bg-white">
            {allNodeSwitches.map((node) => (
              <tr key={node.id} className="even:bg-gray-50">
                <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                  <IndeterminateCheckbox
                    id={`switch-${node.id}`}
                    name={`switch-${node.id}`}
                    checked={selectedNodes.includes(node.id as string)}
                    data-testid={`dialog-package-checkbox-${node.id}`}
                    disabled={
                      (!configurationName[node.id as string] &&
                        !selectedNodes.includes(node.id as string)) ||
                      !(node.data?.category as NodeCategoryModel)?.isValid
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.checked) {
                        setSelectedNodes((old) => [...old, node.id as string]);
                      } else {
                        setSelectedNodes((old) =>
                          old.filter((id) => id !== node.id),
                        );
                      }
                    }}
                  />
                </td>
                <td
                  className={twMerge([
                    'whitespace-nowrap px-3 py-2 text-sm text-gray-900',
                    (node.data?.category as NodeCategoryModel)?.isValid
                      ? ''
                      : 'text-red-500',
                  ])}
                >
                  <label
                    className="cursor-pointer"
                    htmlFor={`switch-${node.id}`}
                    data-testid={`dialog-package-label-${node.id}`}
                  >
                    {(node.data?.category as NodeCategoryModel)?.name} (
                    {node.data?.key as string})
                  </label>
                </td>
                <td className="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                  <button
                    type="button"
                    data-testid={`dialog-package-export-button-${node.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!configurationName[node.id as string]) return;
                      exportNode(node.id as string);
                    }}
                    disabled={
                      !configurationName[node.id as string] ||
                      !(node.data?.category as NodeCategoryModel)?.isValid
                    }
                    className={twMerge([
                      'flex items-center justify-center gap-1 px-2 py-1 font-medium text-white',
                      'font-medium text-indigo-600 hover:text-indigo-500',
                      'disabled:cursor-not-allowed disabled:text-gray-400',
                    ])}
                  >
                    {isExportingIds.includes(node.id as string) ? (
                      <LoadingIcon className="mr-2" />
                    ) : (
                      'Export'
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {allNodeSwitches.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-sm text-gray-700">
                  No switches found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-0 whitespace-nowrap text-sm/6 font-medium text-gray-900">
            <div className="flex flex-row items-center justify-start gap-2">
              <IndeterminateCheckbox
                id="select-all-switches"
                name="select-all-switches"
                data-testid="dialog-package-select-all-switches"
                checked={allNodeSwitches.length === selectedNodes.length}
                indeterminate={
                  selectedNodes.length > 0 &&
                  selectedNodes.length < allNodeSwitches.length
                }
                disabled={allNodeSwitches.length === 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    setSelectedNodes(allNodeSwitches.map((n) => n.id));
                  } else {
                    setSelectedNodes([]);
                  }
                }}
              />
              <label htmlFor="select-all-switches">Select all switches</label>
            </div>
          </div>
          <div className="flex justify-end text-sm/6 text-gray-700 sm:mt-0">
            <Button
              type="button"
              data-testid="dialog-package-export-selected-button"
              onClick={(e) => {
                e.preventDefault();
                exportSelected();
              }}
              disabled={
                allNodeSwitches.length === 0 ||
                selectedNodes.length === 0 ||
                selectedNodes.some((id) => !configurationName[id]) ||
                isExportingIds.length > 0 ||
                selectedNodes.some(
                  (id) =>
                    !(
                      nodes.find((n) => n.id === id)?.data
                        .category as NodeCategoryModel
                    )?.isValid,
                )
              }
              className="flex items-center justify-center"
            >
              {isExportingIds.length > 0 && <LoadingIcon className="mr-2" />}
              {isExportingIds.length > 0 ? 'Exporting...' : 'Export Selected'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
